import { useContext, useState } from "react";
import WellnessProgressTracker from "../../components/WellnessProgressTracker";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Context } from "../../components/Store";
import { useAudioPreference } from "../../hooks/useAudioPreference";
import ReactPlayer from "react-player/lazy";
import { useBootstrap } from "@/features/bootstrap";

// Emotion data structure with main emotions and sub-emotions
const emotionData = {
  down: {
    label: "Feeling down?",
    emotions: [
      {
        main: "sad",
        borderColor: "#BFDBFE",
        color: "#1E3A8A",
        backgroundColor: "#EFF6FF",
        selectedColor: "#1E3A8A",
        image: "../images/emotions/sad.png",
        subEmotions: ["Down", "Hurt", "Depressed"],
      },
      {
        main: "tired",
        borderColor: "#BFDBFE",
        color: "#1E3A8A",
        backgroundColor: "#EFF6FF",
        selectedColor: "#1E3A8A",
        image: "../images/emotions/tired.png",
        subEmotions: ["Sleepy", "Exhausted", "Drained"],
      },
      {
        main: "lonely",
        borderColor: "#BFDBFE",
        color: "#1E3A8A",
        backgroundColor: "#EFF6FF",
        selectedColor: "#1E3A8A",
        image: "../images/emotions/lonely.png",
        subEmotions: ["Alone", "Hopeless", "Isolated"],
      },
    ],
  },
  positive: {
    label: "Feeling centered?",
    emotions: [
      {
        main: "happy",
        color: "#064E3B",
        borderColor: "#A7F3D0",
        backgroundColor: "#ECFDF5",
        selectedColor: "#064E3B",
        image: "../images/emotions/happy.png",
        subEmotions: ["Joyful", "Excited", "Hopeful"],
      },
      {
        main: "grateful",
        color: "#064E3B",
        borderColor: "#A7F3D0",
        backgroundColor: "#ECFDF5",
        selectedColor: "#064E3B",
        image: "../images/emotions/grateful.png",
        subEmotions: ["Thankful", "Fortunate", "Appreciative"],
      },
      {
        main: "calm",
        color: "#064E3B",
        borderColor: "#A7F3D0",
        backgroundColor: "#ECFDF5",
        selectedColor: "#064E3B",
        image: "../images/emotions/calm.png",
        subEmotions: ["Relaxed", "Peaceful", "Balanced"],
      },
    ],
  },
  worried: {
    label: "Feeling upset?",
    emotions: [
      {
        main: "scared",
        color: "#4C1D95",
        borderColor: "#DDD6FE",
        backgroundColor: "#FAF5FF",
        selectedColor: "#4C1D95",
        image: "../images/emotions/scared.png",
        subEmotions: ["Afraid", "Doubtful", "Worried"],
      },
      {
        main: "stressed",
        color: "#4C1D95",
        borderColor: "#DDD6FE",
        backgroundColor: "#FAF5FF",
        selectedColor: "#4C1D95",
        image: "../images/emotions/stressed.png",
        subEmotions: ["Tense", "Nervous", "Overwhelmed"],
      },
      {
        main: "angry",
        color: "#4C1D95",
        borderColor: "#DDD6FE",
        backgroundColor: "#FAF5FF",
        selectedColor: "#4C1D95",
        image: "../images/emotions/angry.png",
        subEmotions: ["Mad", "Frustrated", "Irritated"],
      },
    ],
  },
};

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange }) => (
  <div className="flex items-center gap-2.5 sm:w-full sm:justify-center sm:rounded-md sm:px-0 sm:py-2">
    <span className="text-sm font-medium text-gray-600">Show More Emotions</span>
    <button
      type="button"
      className={`relative h-6 w-11 shrink-0 rounded-full border-0 p-0 transition-colors duration-300 sm:h-[18px] sm:w-8 ${
        checked ? "bg-[#1879EE]" : "bg-gray-400"
      }`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 sm:h-3.5 sm:w-3.5 ${
          checked ? "translate-x-[22px] sm:translate-x-[15px]" : "translate-x-0"
        }`}
      />
    </button>
  </div>
);

// Emotion Card for 9-emotion view
const EmotionCard9 = ({
  emotion,
  color,
  borderColor,
  backgroundColor,
  selectedColor,
  image,
  onClick,
  isHovered,
  onHover,
}) => (
  <div
    className={`flex cursor-pointer flex-col items-center justify-center gap-3
      rounded-lg border-1 px-6 py-4 shadow-sm transition-all duration-200 hover:z-10 
      hover:shadow-md sm:rounded-xl sm:p-5 ${isHovered ? "scale-[1] -translate-y-0.5 shadow-md" : ""
    }`}
    style={{
      borderColor: isHovered ? selectedColor : borderColor,
      backgroundColor: backgroundColor,
    }}
    onClick={() => onClick(emotion)}
    onMouseEnter={() => onHover(emotion)}
    onMouseLeave={() => onHover(null)}
  >
    <img
      src={image}
      alt={emotion}
      className={`h-[5vw] min-h-10 w-[5vw] min-w-10 max-h-[70px] max-w-[70px] object-contain transition-transform duration-200 sm:h-14 sm:min-h-14 sm:w-14 sm:min-w-14 sm:max-h-none sm:max-w-none ${isHovered ? "scale-110" : ""}`}
    />
    <span className="text-base font-semibold tracking-wide sm:text-sm" style={{ color: isHovered ? selectedColor : color }}>
      {emotion.toUpperCase()}
    </span>
  </div>
);

// Emotion Card for 27-emotion view (main emotion with sub-emotions)
const EmotionCard27 = ({
  emotion,
  borderColor,
  color,
  backgroundColor,
  selectedColor,
  image,
  subEmotions,
  onMainClick,
  onSubClick,
  hoveredSub,
  onHoverSub,
  isCardHovered,
  onCardHover,
}) => {
  // Only highlight sub-emotion when directly hovering it
  // When hovering the card (but not a sub), highlight the parent header instead
  const isHeaderHighlighted = isCardHovered && !hoveredSub;

  return (
    <div
      className={`flex flex-col gap-0.5 rounded-lg border-1 p-4 shadow-sm transition-all duration-200 sm:rounded-lg sm:p-3.5 ${isCardHovered ? "scale-[1.03] shadow-lg" : ""}`}
      style={{ borderColor: borderColor, backgroundColor: backgroundColor }}
      onMouseEnter={() => onCardHover(emotion)}
      onMouseLeave={() => {
        onCardHover(null);
        onHoverSub(null);
      }}
    >
      <div
        className={`flex cursor-pointer items-center gap-2 mb-2 rounded-md transition-all duration-200 ${isHeaderHighlighted ? "scale-[1.02] p-2" : ""}`}
        style={isHeaderHighlighted ? { backgroundColor: selectedColor } : {}}
        onClick={() => onMainClick(emotion)}
      >
        <img
          src={image}
          alt={emotion}
          className={`h-[22px] w-[22px] object-contain transition-transform duration-200 sm:h-7 sm:w-7 ${isHeaderHighlighted ? "scale-110" : ""}`}
        />
        <span
          className="text-lg font-medium sm:text-sm"
          style={{ color: isHeaderHighlighted ? "white" : color }}
        >
          {emotion.toUpperCase()}
        </span>
      </div>
      <div className="flex flex-col gap-2 sm:gap-1.5" onMouseLeave={() => onHoverSub(null)}>
        {subEmotions.map((sub) => {
          const isActive = hoveredSub === sub;
          return (
            <button
              key={sub}
              className={`rounded border px-2 py-1 text-center text-lg transition-all duration-200 sm:px-3 sm:py-2.5 sm:text-sm ${
                isActive ? "scale-[1.03] text-white shadow-md" : "border-gray-200 bg-white/80 hover:scale-[1.03]"
              }`}
              style={isActive ? { backgroundColor: selectedColor, borderColor: selectedColor } : {}}
              onClick={() => onSubClick(emotion, sub)}
              onMouseEnter={() => onHoverSub(sub)}
            >
              {sub}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const HowDoIFeel = () => {
  const { data: user } = useBootstrap();
  const navigate = useNavigate();
  const [playing] = useAudioPreference();
  const [, setWellnessDataPoint] = useContext(Context);
  const [showMore, setShowMore] = useState(false);
  const [hoveredEmotion, setHoveredEmotion] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredSub, setHoveredSub] = useState(null);

  const audio_url_prefix = (user?.voice ?? "english female").replace(" ", "_");

  const sendEmotionAndNav = (emotion, subEmotion = null) => {
    setWellnessDataPoint((prev) => ({
      ...prev,
      emotion,
      ...(subEmotion ? { subEmotion: subEmotion.toLowerCase() } : {}),
    }));
    navigate("/deepbreathe2");
  };

  const allEmotions = Object.values(emotionData).flatMap(
    (group) => group.emotions,
  );

  return (
    <div className="grid h-screen grid-rows-[auto_1fr] bg-white">
      <div className="sticky top-0 mt-10 sm:mt-0 z-10 shrink-0 bg-white sm:ml-[130px] px-0 sm:px-8 lg:px-32">
        <div className="flex flex-row items-center justify-center">
          <header className="py-4 text-center sm:px-6 sm:pb-4 sm:pt-12">
            <h1 className="mb-2 text-2xl font-bold md:text-3xl">How do I feel?</h1>
            <p className="mx-auto text-[15px] sm:text-base text-gray-600">Take a moment to check in with yourself</p>
          </header>
          <div className="hidden sm:flex sm:w-auto flex-row items-center justify-end absolute right-0 sm:px-8 xl:px-32">
            <ToggleSwitch checked={showMore} onChange={setShowMore} />
          </div>
        </div>
        <WellnessProgressTracker />
        <div className="flex w-full justify-center py-3 sm:hidden bg-gray-200 rounded">
          <ToggleSwitch checked={showMore} onChange={setShowMore} />
        </div>
      </div>
      <div className="flex flex-col">
        <motion.div>
          <div className="h-0 w-0 overflow-hidden opacity-0">
            <ReactPlayer
              playing={playing}
              url={"../audio/" + audio_url_prefix + "/howdoifeel.wav"}
            />
          </div>

          <div className="flex flex-col sm:ml-[130px] mt-6 px-4 sm:px-8 lg:px-32 max-h-screen overflow-y-auto">
            {!showMore ? (
              <div className="my-4 flex w-full flex-col pb-5">
                <div className="grid grid-cols-3 gap-4 sm:grid-cols-3 sm:gap-8">
                  {allEmotions.map(({ main, color, borderColor, backgroundColor, selectedColor, image }) => (
                    <EmotionCard9
                      key={main}
                      emotion={main}
                      color={color}
                      borderColor={borderColor}
                      backgroundColor={backgroundColor}
                      selectedColor={selectedColor}
                      image={image}
                      onClick={sendEmotionAndNav}
                      isHovered={hoveredEmotion === main}
                      onHover={setHoveredEmotion}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-4 flex w-full flex-col pb-5">
                <div className="flex flex-col gap-5 sm:gap-3">
                  {Object.entries(emotionData).map(([key, group]) => (
                    <div key={key} className="w-full">
                      <h2 className="mb-2 text-lg font-medium opacity-80 sm:text-base">{group.label}</h2>
                      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                        {group.emotions.map(({ main, color, borderColor, backgroundColor, selectedColor, image, subEmotions }) => (
                          <EmotionCard27
                            key={main}
                            emotion={main}
                            color={color}
                            borderColor={borderColor}
                            backgroundColor={backgroundColor}
                            selectedColor={selectedColor}
                            image={image}
                            subEmotions={subEmotions}
                            onMainClick={sendEmotionAndNav}
                            onSubClick={sendEmotionAndNav}
                            hoveredSub={hoveredCard === main ? hoveredSub : null}
                            onHoverSub={setHoveredSub}
                            isCardHovered={hoveredCard === main}
                            onCardHover={setHoveredCard}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HowDoIFeel;
